import React, {useMemo, useState} from 'react';
import Page from "components/Page";
import Card from "components/Card";
import StatList from "components/StatList";
import StatBox from "components/StatBox";
import {ChevronDoubleDownIcon, ChevronDoubleUpIcon, ClockIcon, FastForwardIcon, LightningBoltIcon} from "@heroicons/react/outline";
import {LightningBoltIcon as LightningBoltIconSolid} from "@heroicons/react/solid";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {dateString, defaultFilters} from 'services/insight';
import InsightControls from "components/InsightControls";
import RoleSelect from "components/InsightControls/RoleSelect";
import SiteSelect from "components/InsightControls/SiteSelect";
import {truncate} from "services/string";
import DailyTriggerExecutionsChart from "components/Chart/DailyTriggerExecutionsChart";
import HourlyTriggerExecutionsChart from "components/Chart/HourlyTriggerExecutionsChart";
import TriggerExecutionsChart from "components/Chart/TriggerExecutionsChart";

import type {InsightFilters, TriggerInsightFilters} from "types/OperatorInsights";
import type {Role} from "types/Role";
import type {Site} from "types/Site";
import ExportButton from "../../../components/ExportButton";
import {useOperator} from "../../../context/OperatorContext";

// Helpers ----

const breadcrumbs = {"Performance": "/", "Triggers": "/insights/trigger"}

// Component ----

function TriggerInsights() {
  const {termFor} = useOperator();
  const [filters, setFilters] = useState<TriggerInsightFilters>({...defaultFilters, role: null});
  const [site, setSite] = useState<Site | null>(null);

  const sinceInTimezone = useMemo(() => filters.since?.setZone(site?.timezone ?? 'local', { keepLocalTime: true}), [site, filters.since]);
  const untilInTimezone = useMemo(() => filters.until?.setZone(site?.timezone ?? 'local', { keepLocalTime: true}), [site, filters.until]);

  const {data, isLoading, isFetching} = useQuery(
    ['insights', 'triggers', {...filters, since: sinceInTimezone, until: untilInTimezone}],
    ({signal}) => ngage.insights.triggers({...filters, since: sinceInTimezone, until: untilInTimezone}, {signal}),
    {
      keepPreviousData: true,
      enabled: !!sinceInTimezone && !!untilInTimezone,
    }
  )

  function changeStandardFilters(standardFilters: InsightFilters) {
    setFilters({...filters, ...standardFilters})
  }

  function selectSite(site: Site | null) {
    setSite(site);
    setFilters({...filters, site: site?.id ?? null})
  }

  function selectRole(role: Role | null) {
    setFilters({...filters, role: role?.id ?? null})
  }

  const sinceDateString = filters.since.toFormat('dd/MM/yyyy');
  const untilDateString = filters.until.toFormat('dd/MM/yyyy');

  const actions = (
    <ExportButton
      getData={() => ngage.insights.triggersExport(filters, 'csv')}
      dataType={'text/csv'}
      prefix={'ngage-trigger-insights'}
      extension={'csv'}
    />
  );

  return (
    <Page
      title="Triggers"
      subTitle='Understand more about how app users are interacting with your triggers'
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoading}
    >

      {/* Controls */}
      <Card isLoading={isFetching}>
        <InsightControls filters={filters} onChange={changeStandardFilters}>

          {/* Context-specific controls */}
          <SiteSelect id="select-site" name="site" label={termFor('Site')} onChange={selectSite}/>
          <RoleSelect id="select-role" name="role" label="Role" onChange={selectRole}/>
        </InsightControls>
      </Card>

      {/* Stats */}
      <div className="mt-4">
        <StatList columns={3}>
          <StatBox
            name="Triggers"
            icon={LightningBoltIcon}
            stat={data?.summary.total_triggers.toLocaleString()}
            change={data?.summary.new_triggers.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of triggers added as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
          />

          <StatBox
            name="Enabled Triggers"
            icon={LightningBoltIconSolid}
            stat={data?.summary.total_enabled_triggers.toLocaleString()}
            change={data?.summary.new_enabled_triggers.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of triggers enabled as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
          />

          <StatBox
            name="Trigger Executions"
            icon={FastForwardIcon}
            stat={data?.summary.total_trigger_executions.toLocaleString()}
            change={data?.summary.new_trigger_executions.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of times triggers ran as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
          />

          <StatBox
            name="Least Executed Trigger"
            icon={ChevronDoubleDownIcon}
            stat={truncate(data?.summary.least_executed_trigger?.name ?? 'N/A', 35)}
            isLoading={isLoading}
            tooltip={`The trigger that has run the fewest times between ${sinceDateString} and ${untilDateString}.`}
          />

          <StatBox
            name="Most Executed Trigger"
            icon={ChevronDoubleUpIcon}
            stat={truncate(data?.summary.most_executed_trigger?.name ?? 'N/A', 35)}
            isLoading={isLoading}
            tooltip={`The trigger that has run the most times between ${sinceDateString} and ${untilDateString}.`}
          />

          <StatBox
            name="Recently Executed Trigger"
            icon={ClockIcon}
            stat={truncate(data?.summary.recently_executed_trigger?.name ?? 'N/A', 35)}
            isLoading={isLoading}
            tooltip={`The trigger that has run the most recently between ${sinceDateString} and ${untilDateString}.`}
          />
        </StatList>
      </div>

      {/* Charts */}
      <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-2">

        <Card
          heading="Daily Trigger Executions"
          subheading={`Trigger Executions per weekday between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          <div className="h-96">
            {data && <DailyTriggerExecutionsChart data={data}/>}
          </div>
        </Card>

        <Card
          heading="Hourly Trigger Executions"
          subheading={`Trigger Executions per hour between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          <div className="h-96">
            {data && <HourlyTriggerExecutionsChart data={data}/>}
          </div>
        </Card>

        <Card
          heading="Trigger Executions"
          subheading={`Total trigger executions per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <TriggerExecutionsChart data={data} interval={filters.interval} compare={filters.compare}/>}
        </Card>
      </div>
    </Page>
  );
}

export default TriggerInsights;